import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSpotRequestStore } from '../store/spotRequestStore';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { useTranslation } from '../i18n';
import { apiClient } from '../api/client';
import { analytics } from '../hooks/useAnalytics';
import * as spotRequestApi from '../api/spotRequests';
import { BottomNav } from '../components/ui/BottomNav';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { formatLocalTime, isoToDatetimeLocal } from '../utils/time';
import { localToUTC } from '../lib/timezone';
import type { SpotRequest, SpotOffer, SpotRequestDetail, SpotRequestSchedule } from '../store/spotRequestStore';

const EXPIRING_SOON_MS = 30 * 60 * 1000;

type UnifiedRequest = SpotRequest & {
  is_mine: boolean;
  requester_apartment?: string;
  requester_name?: string;
};

type DerivedStatus = 'open' | 'waiting' | 'matched' | 'expiring';

function deriveStatus(req: UnifiedRequest, now: number): DerivedStatus {
  const untilMs = new Date(req.requested_until).getTime();
  const msLeft = untilMs - now;

  if (req.is_mine) {
    if (msLeft > 0 && msLeft <= EXPIRING_SOON_MS) return 'expiring';
    if (req.status === 'accepted') return 'matched';
    if ((req.offer_count ?? 0) > 0) return 'matched';
    return 'waiting';
  }
  if (msLeft > 0 && msLeft <= EXPIRING_SOON_MS) return 'expiring';
  return 'open';
}

const STATUS_CHIP_CLASS: Record<DerivedStatus, string> = {
  open:     'chip chip-open',
  waiting:  'chip chip-waiting',
  matched:  'chip chip-matched',
  expiring: 'chip chip-expiring',
};

const STATUS_TRANSLATION_KEY: Record<DerivedStatus, string> = {
  open:     'status_open',
  waiting:  'status_waiting',
  matched:  'status_matched',
  expiring: 'status_expiring',
};

export function SpotRequestPage() {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { t, language } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [mySpots, setMySpots] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [openedRequest, setOpenedRequest] = useState<UnifiedRequest | null>(null);
  const [offeringSpotId, setOfferingSpotId] = useState('');
  const [now, setNow] = useState(Date.now());

  const [schedules, setSchedules] = useState<SpotRequestSchedule[]>([]);
  const [showCreateScheduleSheet, setShowCreateScheduleSheet] = useState(false);
  const [scheduleCreateLoading, setScheduleCreateLoading] = useState(false);
  const [scheduleActionId, setScheduleActionId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const myRequest = useSpotRequestStore((s) => s.myRequest);
  const myRequestDetail = useSpotRequestStore((s) => s.myRequestDetail);
  const incomingRequests = useSpotRequestStore((s) => s.incomingRequests);
  const setMyRequest = useSpotRequestStore((s) => s.setMyRequest);
  const setMyRequestDetail = useSpotRequestStore((s) => s.setMyRequestDetail);
  const setIncomingRequests = useSpotRequestStore((s) => s.setIncomingRequests);
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');

  // tick clock every 30s so status chips ("expiring soon") refresh and list resorts
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    loadAll();
    loadMySpots();
    loadSchedules();
  }, []);

  useEffect(() => {
    if (myRequest && ['pending', 'accepted'].includes(myRequest.status)) {
      loadRequestDetail(myRequest.id);
    } else {
      setMyRequestDetail(null);
    }
  }, [myRequest?.id, myRequest?.status]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [mine, incoming] = await Promise.all([
        spotRequestApi.getMyRequests(1),
        spotRequestApi.getIncomingRequests(1),
      ]);
      setMyRequest(mine.data[0] ?? null);
      if (!mine.data[0]) setMyRequestDetail(null);
      setIncomingRequests(incoming.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }

  async function loadMySpots() {
    try {
      const response = await apiClient.get<{ data: any[] }>('/spots/mine');
      setMySpots(response.data.data || []);
    } catch (err) {
      console.error('Failed to load spots:', err);
    }
  }

  async function loadRequestDetail(requestId: string) {
    try {
      const detail = await spotRequestApi.getRequestDetail(requestId);
      setMyRequestDetail(detail);
    } catch (err) {
      console.error('Failed to load request detail:', err);
    }
  }

  async function loadSchedules() {
    try {
      const result = await spotRequestApi.getRequestSchedules();
      setSchedules(result);
    } catch (err) {
      console.error('Failed to load request schedules:', err);
    }
  }

  // unified, sorted list ----------------------------------------------------
  const unified: UnifiedRequest[] = useMemo(() => {
    const out: UnifiedRequest[] = [];
    if (myRequest && ['pending', 'accepted'].includes(myRequest.status)) {
      out.push({ ...myRequest, is_mine: true });
    }
    for (const r of incomingRequests ?? []) {
      if (r.status !== 'pending') continue;
      out.push({
        ...r,
        is_mine: false,
        requester_apartment: (r as any).requester_apartment,
        requester_name: (r as any).requester_name,
      });
    }
    // sort by expiry asc, regardless of ownership
    out.sort((a, b) => new Date(a.requested_until).getTime() - new Date(b.requested_until).getTime());
    return out;
  }, [myRequest, incomingRequests]);

  // actions -----------------------------------------------------------------
  async function handleCancelRequest() {
    if (!myRequest) return;
    setLoading(true);
    setError(null);
    try {
      await spotRequestApi.cancelSpotRequest(myRequest.id);
      analytics.trackSpotRequestCancelled();
      setMyRequest(null);
      setMyRequestDetail(null);
      setOpenedRequest(null);
      setIsEditMode(false);
      void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel request');
    } finally {
      setLoading(false);
    }
  }

  async function handleAcceptOffer(offerId: string) {
    setLoading(true);
    setError(null);
    try {
      await spotRequestApi.acceptSpotOffer(offerId);
      setMyRequest(null);
      setMyRequestDetail(null);
      setOpenedRequest(null);
      await queryClient.invalidateQueries({ queryKey: ['session', 'active'] });
      await queryClient.invalidateQueries({ queryKey: ['spots', 'free'] });
      await queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept offer');
    } finally {
      setLoading(false);
    }
  }

  async function handleOfferOnRequest(req: UnifiedRequest, spotId: string) {
    setCreateLoading(true);
    setError(null);
    try {
      const fromISO = new Date().toISOString();
      const untilISO = req.requested_until;
      await spotRequestApi.createSpotOffer(req.id, spotId, fromISO, untilISO);
      analytics.trackOfferMade(spotId, req.requester_apartment);
      setOpenedRequest(null);
      setOfferingSpotId('');
      await loadAll();
      void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
    } catch (err) {
      let msg = 'Failed to create offer';
      if (err && typeof err === 'object' && 'response' in err) {
        const ax = err as any;
        const code = ax.response?.data?.error?.code as string | undefined;
        // Translate known error codes to localised text. The server returns
        // English messages by design — translation is per-locale on the
        // client. Fall through to the server message only for codes we
        // haven't localised yet.
        if (code === 'OFFER_ALREADY_EXISTS') {
          msg = t('offer_already_exists');
        } else if (code === 'SPOT_REQUEST_FULFILLED') {
          // Another neighbour's offer was accepted while this user was filling
          // out the form. Drop the request from view + close the offer form so
          // they don't try again on a dead row.
          msg = t('request_already_fulfilled');
          setIncomingRequests((incomingRequests ?? []).filter((r) => r.id !== req.id));
          setOpenedRequest(null);
          setOfferingSpotId('');
        } else if (ax.response?.status === 410) {
          msg = t('request_no_longer_available');
          setIncomingRequests((incomingRequests ?? []).filter((r) => r.id !== req.id));
          setOpenedRequest(null);
          setOfferingSpotId('');
        } else if (ax.response?.data?.error?.message) {
          msg = ax.response.data.error.message;
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleIgnoreRequest(req: UnifiedRequest) {
    setIncomingRequests((incomingRequests ?? []).filter((r) => r.id !== req.id));
    analytics.trackRequestIgnored(req.requester_apartment);
    setOpenedRequest(null);
  }

  async function handleCreateRequest(e: React.FormEvent) {
    e.preventDefault();
    setCreateLoading(true);
    setError(null);
    try {
      if (!from || !until) {
        setError(t('fill_all_fields'));
        setCreateLoading(false);
        return;
      }
      const fromISO = localToUTC(from, timezone);
      const untilISO = localToUTC(until, timezone);

      if (isEditMode && myRequest) {
        await spotRequestApi.editSpotRequest(myRequest.id, fromISO, untilISO);
        setIsEditMode(false);
      } else {
        await spotRequestApi.createSpotRequest(fromISO, untilISO);
        analytics.trackSpotRequestCreated();
      }
      await loadAll();
      void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
      setShowCreateSheet(false);
      setFrom('');
      setUntil('');
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'edit' : 'create'} request`);
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleQuickRequest(hours: number) {
    setCreateLoading(true);
    setError(null);
    try {
      const nowDate = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
      const toLocal = (d: Date) => {
        const p = formatter.formatToParts(d);
        const get = (k: string) => p.find((x) => x.type === k)?.value;
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
      };
      const fromISO = localToUTC(toLocal(nowDate), timezone);
      const untilISO = localToUTC(toLocal(new Date(nowDate.getTime() + hours * 3600_000)), timezone);
      await spotRequestApi.createSpotRequest(fromISO, untilISO);
      analytics.trackSpotRequestCreated();
      await loadAll();
      void queryClient.invalidateQueries({ queryKey: ['spot-requests'] });
      setShowCreateSheet(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create request');
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleCreateSchedule(daysOfWeek: number[], startTime: string, endTime: string) {
    setScheduleCreateLoading(true);
    setError(null);
    try {
      await spotRequestApi.createRequestSchedule(daysOfWeek, startTime, endTime);
      analytics.trackSpotRequestScheduleCreated();
      await loadSchedules();
      setShowCreateScheduleSheet(false);
    } catch (err) {
      let msg = 'Failed to create schedule';
      if (err && typeof err === 'object' && 'response' in err) {
        const ax = err as any;
        msg = ax.response?.data?.error?.message ?? msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setScheduleCreateLoading(false);
    }
  }

  async function handleToggleSchedule(schedule: SpotRequestSchedule) {
    setScheduleActionId(schedule.id);
    setError(null);
    try {
      const updated = await spotRequestApi.setRequestScheduleActive(schedule.id, !schedule.is_active);
      analytics.trackSpotRequestScheduleToggled(updated.is_active);
      setSchedules((prev) => prev.map((s) => (s.id === schedule.id ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
    } finally {
      setScheduleActionId(null);
    }
  }

  async function handleDeleteSchedule(scheduleId: string) {
    setScheduleActionId(scheduleId);
    setError(null);
    try {
      await spotRequestApi.deleteRequestSchedule(scheduleId);
      analytics.trackSpotRequestScheduleDeleted();
      setSchedules((prev) => prev.filter((s) => s.id !== scheduleId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
    } finally {
      setScheduleActionId(null);
    }
  }

  const hasActiveOwn = !!(myRequest && ['pending', 'accepted'].includes(myRequest.status));

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>
      <header className="topbar pt-[env(safe-area-inset-top,0px)] h-[calc(64px+env(safe-area-inset-top,0px))]" style={{
        position: 'sticky',
        top: 0, 
        zIndex: 50,
        //height: '64px',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => navigate('/profile')} style={iconBtn} title={t('settings')} aria-label={t('settings')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>settings</span>
          </button>
          <button onClick={() => setShowSignOutConfirm(true)} style={iconBtn} title={t('sign_out')} aria-label={t('sign_out')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
          </button>
        </div>
      </header>

      <div className="page-body">
        <section style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 style={{ fontFamily: 'var(--manrope)', fontSize: '28px', fontWeight: 800, color: 'var(--on-surface)', letterSpacing: '-0.02em', margin: 0, lineHeight: 1.15 }}>
              {t('spot_requests')}
            </h2>
            {unified.length > 0 && (
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', background: 'var(--primary-container)', padding: '5px 12px', borderRadius: '9999px', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '12px' }}>
                {unified.length}
              </span>
            )}
          </div>
          <p style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, margin: 0 }}>
            {t('manage_requests')}
          </p>
        </section>

        <RecurringSchedulesSection
          schedules={schedules}
          language={language}
          actionId={scheduleActionId}
          t={t}
          onCreate={() => setShowCreateScheduleSheet(true)}
          onToggle={handleToggleSchedule}
          onDelete={handleDeleteSchedule}
        />

        {error && (
          <div style={{
            background: '#fe8983', color: '#752121', padding: '12px 16px',
            borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
          }}>
            {error}
          </div>
        )}

        {loading && unified.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--on-surface-3)', padding: '48px 0' }}>{t('loading')}</div>
        ) : unified.length === 0 ? (
          <RequestsEmptyState onCreate={() => setShowCreateSheet(true)} t={t} />
        ) : (
          <>
            {unified.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                now={now}
                timezone={timezone}
                language={language}
                t={t}
                onOpen={() => setOpenedRequest(req)}
              />
            ))}
            {!hasActiveOwn && (
              <button
                onClick={() => setShowCreateSheet(true)}
                style={{
                  width: '100%', height: '56px', borderRadius: '20px',
                  border: '2px dashed #c7d5e8', background: 'none',
                  color: 'var(--primary)', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', marginTop: '4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>add</span>
                {t('create_request_cta')}
              </button>
            )}
          </>
        )}
      </div>

      <BottomNav />

      {/* Detail sheet */}
      {openedRequest && (
        <DetailSheet
          req={openedRequest}
          now={now}
          timezone={timezone}
          language={language}
          mySpots={mySpots}
          myRequestDetail={openedRequest.is_mine ? myRequestDetail : null}
          isLoading={loading}
          createLoading={createLoading}
          offeringSpotId={offeringSpotId}
          setOfferingSpotId={setOfferingSpotId}
          onClose={() => { setOpenedRequest(null); setOfferingSpotId(''); }}
          onCancel={handleCancelRequest}
          onAcceptOffer={handleAcceptOffer}
          onEdit={() => {
            if (!myRequest) return;
            setIsEditMode(true);
            setFrom(isoToDatetimeLocal(myRequest.requested_from, timezone));
            setUntil(isoToDatetimeLocal(myRequest.requested_until, timezone));
            setOpenedRequest(null);
            setShowCreateSheet(true);
          }}
          onOffer={(spotId) => handleOfferOnRequest(openedRequest, spotId)}
          onIgnore={() => handleIgnoreRequest(openedRequest)}
          t={t}
        />
      )}

      {/* Create / edit sheet */}
      {showCreateSheet && (
        <CreateSheet
          from={from} setFrom={setFrom}
          until={until} setUntil={setUntil}
          isEditMode={isEditMode}
          createLoading={createLoading}
          onSubmit={handleCreateRequest}
          onQuickRequest={handleQuickRequest}
          onClose={() => {
            setShowCreateSheet(false);
            setIsEditMode(false);
            setFrom(''); setUntil('');
          }}
          t={t}
        />
      )}

      {/* Create request schedule sheet */}
      {showCreateScheduleSheet && (
        <CreateScheduleSheet
          language={language}
          createLoading={scheduleCreateLoading}
          onSubmit={handleCreateSchedule}
          onClose={() => setShowCreateScheduleSheet(false)}
          t={t}
        />
      )}

      {/* Sign out dialog */}
      {showSignOutConfirm && (
        <>
          <div onClick={() => setShowSignOutConfirm(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'calc(100% - 48px)', maxWidth: '320px', backgroundColor: '#ffffff', borderRadius: '20px', padding: '28px 20px 20px', zIndex: 301, boxShadow: '0 16px 40px -4px rgba(43,52,55,0.18)' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span className="material-symbols-outlined" style={{ color: '#dc2626', fontSize: '24px' }}>logout</span>
            </div>
            <div style={{ fontFamily: 'var(--manrope)', fontSize: '17px', fontWeight: 700, textAlign: 'center', marginBottom: '8px', color: 'var(--on-surface)' }}>
              {t('sign_out')} {t('of_app')}?
            </div>
            <div style={{ fontSize: '13px', color: 'var(--on-surface-2)', textAlign: 'center', marginBottom: '24px', lineHeight: 1.5 }}>
              {t('sign_out_desc')}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowSignOutConfirm(false)} style={{ flex: 1, height: '44px', borderRadius: '10px', border: '1.5px solid var(--primary)', background: '#ffffff', color: 'var(--primary)', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                {t('cancel')}
              </button>
              <button onClick={signOut} style={{ flex: 1, height: '44px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#ffffff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                {t('sign_out')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── RequestCard ──────────────────────────────────────────────────────────────

function RequestCard({
  req, now, timezone, language, t, onOpen,
}: {
  req: UnifiedRequest;
  now: number;
  timezone: string;
  language: 'en' | 'he';
  t: ReturnType<typeof useTranslation>['t'];
  onOpen: () => void;
}) {
  const status = deriveStatus(req, now);
  const requestedFrom = new Date(req.requested_from);
  const requestedUntil = new Date(req.requested_until);

  // For the "not mine" branch we render apartment + name on two separate
  // lines with explicit labels (no dash separator). The name line is omitted
  // when no name is available.
  const isMine = req.is_mine;
  const titleLine1 = isMine
    ? t('my_request')
    : `${t('asking_apartment')}: ${req.requester_apartment ?? ''}`.trim();
  const titleLine2 = isMine || !req.requester_name
    ? null
    : `${t('requester_name')}: ${req.requester_name}`;

  // Always render `from - until` in logical order. The previous Hebrew swap
  // tried to compensate for RTL flipping by manually reversing the operands,
  // but combined with the browser's bidi algorithm and the LTR-internal
  // numeric times it produced the opposite of what was intended. In an RTL
  // container, logical "from - until" displays visually as until-on-the-left,
  // from-on-the-right — which reads correctly right-to-left.
  const rangeLine =
    `${formatLocalTime(requestedFrom.toISOString(), timezone, 'datetime-full', language)} - ${formatLocalTime(requestedUntil.toISOString(), timezone, 'time')}`;

  return (
    <div
      onClick={onOpen}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 8px 24px -4px rgba(43,52,55,0.06)',
        padding: '18px 20px',
        marginBottom: '12px',
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {req.is_mine && (
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0, left: 0,
          width: '4px',
          background: 'var(--primary)',
        }} />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, marginRight: '12px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '16px', color: 'var(--on-surface)' }}>
              {titleLine1}
            </div>
            {req.is_mine && (
              <span className="chip chip-yours">{t('yours_tag').toUpperCase()}</span>
            )}
          </div>
          {titleLine2 && (
            <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '16px', color: 'var(--on-surface)', marginBottom: '4px' }}>
              {titleLine2}
            </div>
          )}
          <div style={{ fontSize: '13px', color: 'var(--on-surface-2)', marginBottom: '2px' }}>
            {t('parking_period')}: {rangeLine}
          </div>
          {req.is_mine && (req.offer_count ?? 0) > 0 && (
            <div style={{ fontSize: '12px', color: '#166534', fontWeight: 600, marginTop: '4px' }}>
              {req.offer_count} {req.offer_count === 1 ? t('offer') : t('offers')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span className={STATUS_CHIP_CLASS[status]}>
            {t(STATUS_TRANSLATION_KEY[status]).toUpperCase()}
          </span>
          <span style={{ color: 'var(--on-surface-3)', fontSize: '18px', lineHeight: 1 }}>›</span>
        </div>
      </div>
    </div>
  );
}

// ─── RequestsEmptyState ───────────────────────────────────────────────────────

function RequestsEmptyState({ onCreate, t }: { onCreate: () => void; t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div style={{
      background: 'var(--surface-low)',
      borderRadius: '24px',
      padding: '40px 24px',
      textAlign: 'center',
      boxShadow: '0 8px 24px -4px rgba(43,52,55,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: '88px', height: '88px', borderRadius: '9999px',
            background: 'var(--surface-high)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '44px', color: 'var(--on-surface-3)' }}>
              person_search
            </span>
          </div>
        </div>
      </div>
      <h2 style={{ fontFamily: 'var(--manrope)', fontSize: '22px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '10px' }}>
        {t('no_requests_empty')}
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, marginBottom: '28px', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto' }}>
        {t('no_requests_empty_desc')}
      </p>
      <button
        onClick={onCreate}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          margin: '0 auto', padding: '14px 32px', borderRadius: '12px', border: 'none',
          background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
          color: '#f7f7ff', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,91,192,0.25)',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>
        {t('create_request_cta')}
      </button>
    </div>
  );
}

// ─── recurring request schedules ─────────────────────────────────────────────

function getScheduleDays(t: ReturnType<typeof useTranslation>['t'], language?: string) {
  if (language === 'he') {
    return [
      { label: 'א', full: t('day_sun'), dayNum: 7 },
      { label: 'ב', full: t('day_mon'), dayNum: 1 },
      { label: 'ג', full: t('day_tue'), dayNum: 2 },
      { label: 'ד', full: t('day_wed'), dayNum: 3 },
      { label: 'ה', full: t('day_thu'), dayNum: 4 },
      { label: 'ו', full: t('day_fri'), dayNum: 5 },
      { label: 'ש', full: t('day_sat'), dayNum: 6 },
    ];
  }
  return [
    { label: 'M', full: t('day_mon'), dayNum: 1 },
    { label: 'T', full: t('day_tue'), dayNum: 2 },
    { label: 'W', full: t('day_wed'), dayNum: 3 },
    { label: 'T', full: t('day_thu'), dayNum: 4 },
    { label: 'F', full: t('day_fri'), dayNum: 5 },
    { label: 'S', full: t('day_sat'), dayNum: 6 },
    { label: 'S', full: t('day_sun'), dayNum: 7 },
  ];
}

function formatScheduleDays(days: number[], t: ReturnType<typeof useTranslation>['t'], language?: string): string {
  if (!days.length) return '';
  const dayMap = new Map(getScheduleDays(t, language).map((d) => [d.dayNum, d.full]));
  const sorted = [...days].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0], end = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? dayMap.get(start)! : `${dayMap.get(start)}–${dayMap.get(end)}`);
      if (i < sorted.length) { start = sorted[i]; end = sorted[i]; }
    }
  }
  return ranges.join(', ');
}

function RecurringSchedulesSection({
  schedules, language, actionId, t, onCreate, onToggle, onDelete,
}: {
  schedules: SpotRequestSchedule[];
  language: 'en' | 'he';
  actionId: string | null;
  t: ReturnType<typeof useTranslation>['t'];
  onCreate: () => void;
  onToggle: (schedule: SpotRequestSchedule) => void;
  onDelete: (scheduleId: string) => void;
}) {
  return (
    <section style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h3 style={{ fontFamily: 'var(--manrope)', fontSize: '15px', fontWeight: 700, color: 'var(--on-surface)', margin: 0 }}>
          {t('recurring_requests')}
        </h3>
        <button
          onClick={onCreate}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--primary)', fontSize: '13px', fontWeight: 700, padding: '4px',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>add</span>
          {t('create_schedule_cta')}
        </button>
      </div>

      {schedules.length === 0 ? (
        <div style={{
          background: 'var(--surface-low)', borderRadius: '16px',
          padding: '16px 18px', fontSize: '13px', color: 'var(--on-surface-3)',
        }}>
          {t('no_schedules_yet')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {schedules.map((schedule) => (
            <div key={schedule.id} style={{
              backgroundColor: '#ffffff', borderRadius: '16px',
              boxShadow: '0 8px 24px -4px rgba(43,52,55,0.06)',
              padding: '14px 16px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: '12px',
              opacity: schedule.is_active ? 1 : 0.55,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--on-surface)' }}>
                  {formatScheduleDays(schedule.days_of_week, t, language)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--on-surface-3)', marginTop: '2px' }}>
                  {schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)}
                  {!schedule.is_active && ` · ${t('schedule_paused')}`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <button
                  onClick={() => onToggle(schedule)}
                  disabled={actionId === schedule.id}
                  title={schedule.is_active ? t('pause') : t('resume')}
                  style={{
                    background: 'none', border: 'none', cursor: actionId === schedule.id ? 'default' : 'pointer',
                    color: 'var(--on-surface-3)', padding: '6px',
                    opacity: actionId === schedule.id ? 0.5 : 1,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                    {schedule.is_active ? 'pause_circle' : 'play_circle'}
                  </span>
                </button>
                <button
                  onClick={() => onDelete(schedule.id)}
                  disabled={actionId === schedule.id}
                  title={t('delete')}
                  style={{
                    background: 'none', border: 'none', cursor: actionId === schedule.id ? 'default' : 'pointer',
                    color: '#dc2626', padding: '6px',
                    opacity: actionId === schedule.id ? 0.5 : 1,
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CreateScheduleSheet({
  language, createLoading, onSubmit, onClose, t,
}: {
  language: 'en' | 'he';
  createLoading: boolean;
  onSubmit: (daysOfWeek: number[], startTime: string, endTime: string) => void;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('18:00');

  const toggleDay = (dayNum: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayNum) ? prev.filter((d) => d !== dayNum) : [...prev, dayNum],
    );
  };

  const isTimeValid = endTime > startTime;
  const canSubmit = selectedDays.length > 0 && isTimeValid && !createLoading;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(selectedDays, startTime, endTime);
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff',
        borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
        padding: '20px 24px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex: 301,
        maxWidth: '430px', margin: '0 auto',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 -8px 32px -4px rgba(43,52,55,0.18)',
      }}>
        <div style={{ width: '40px', height: '4px', background: '#e3e9ec', borderRadius: '2px', margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'var(--manrope)', fontSize: '20px', fontWeight: 700, color: 'var(--on-surface)', margin: 0 }}>
            {t('create_schedule_cta')}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--on-surface-3)' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--on-surface)' }}>
              {t('repeat_on')}
            </label>
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'space-between' }}>
              {getScheduleDays(t, language).map(({ label, full, dayNum }) => {
                const isSelected = selectedDays.includes(dayNum);
                return (
                  <button
                    key={dayNum}
                    type="button"
                    onClick={() => toggleDay(dayNum)}
                    aria-label={full}
                    title={full}
                    style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      border: 'none', background: isSelected ? '#005bc0' : '#d1d5db',
                      color: '#ffffff', fontSize: '13px', fontWeight: 700,
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--on-surface)' }}>{t('from')}</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: '1px solid #abb3b7', fontSize: '14px', boxSizing: 'border-box',
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--on-surface)' }}>{t('until')}</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: '1px solid #abb3b7', fontSize: '14px', boxSizing: 'border-box',
              }} />
            </div>
          </div>
          {!isTimeValid && (
            <div style={{ fontSize: '12px', color: '#dc2626' }}>{t('invalid_time_range')}</div>
          )}

          <button type="submit" disabled={!canSubmit} style={{
            padding: '14px', borderRadius: '10px', border: 'none',
            background: 'var(--primary)', color: '#fff',
            fontSize: '15px', fontWeight: 700,
            cursor: canSubmit ? 'pointer' : 'default',
            opacity: canSubmit ? 1 : 0.5,
            marginTop: '4px',
          }}>
            {createLoading ? t('creating') : t('create_schedule_cta')}
          </button>
        </form>
      </div>
    </>
  );
}

// ─── DetailSheet ──────────────────────────────────────────────────────────────

function DetailSheet({
  req, now, timezone, language, mySpots, myRequestDetail,
  isLoading, createLoading, offeringSpotId, setOfferingSpotId,
  onClose, onCancel, onAcceptOffer, onEdit, onOffer, onIgnore, t,
}: {
  req: UnifiedRequest;
  now: number;
  timezone: string;
  language: 'en' | 'he';
  mySpots: any[];
  myRequestDetail: SpotRequestDetail | null;
  isLoading: boolean;
  createLoading: boolean;
  offeringSpotId: string;
  setOfferingSpotId: (id: string) => void;
  onClose: () => void;
  onCancel: () => void;
  onAcceptOffer: (offerId: string) => void;
  onEdit: () => void;
  onOffer: (spotId: string) => void;
  onIgnore: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const status = deriveStatus(req, now);
  const requestedFrom = new Date(req.requested_from);
  const requestedUntil = new Date(req.requested_until);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff',
        borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
        padding: '20px 24px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex: 301,
        maxWidth: '430px', margin: '0 auto',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 -8px 32px -4px rgba(43,52,55,0.18)',
      }}>
        <div style={{ width: '40px', height: '4px', background: '#e3e9ec', borderRadius: '2px', margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontFamily: 'var(--manrope)', fontSize: '20px', fontWeight: 700, color: 'var(--on-surface)', margin: '0 0 6px' }}>
              {req.is_mine
                ? t('my_request')
                : `${t('apt_label') || 'Apt'} ${req.requester_apartment ?? ''}${req.requester_name ? ' — ' + req.requester_name : ''}`}
            </h3>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span className={STATUS_CHIP_CLASS[status]}>{t(STATUS_TRANSLATION_KEY[status]).toUpperCase()}</span>
              {req.is_mine && <span className="chip chip-yours">{t('yours_tag').toUpperCase()}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--on-surface-3)' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
          </button>
        </div>

        <div style={{ background: 'var(--surface-low)', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--on-surface-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
            {t('requested_for')}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--on-surface)' }}>
            {`${formatLocalTime(requestedFrom.toISOString(), timezone, 'datetime-full', language)} - ${formatLocalTime(requestedUntil.toISOString(), timezone, 'time')}`}
          </div>
        </div>

        {req.is_mine ? (
          <>
            {myRequestDetail && myRequestDetail.offers && myRequestDetail.offers.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--on-surface-3)', margin: '0 0 12px' }}>
                  {t('offers_received')}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {myRequestDetail.offers.map((offer: SpotOffer) => (
                    <div key={offer.id} style={{
                      background: offer.status === 'pending' ? '#eaeff1' : '#f8f9fa',
                      padding: '14px 16px', borderRadius: '12px',
                      borderLeft: offer.status === 'pending' ? '4px solid #10b981' : '4px solid transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--on-surface)' }}>
                          Apt {offer.offerer_apartment} {offer.offerer_name ? '— ' + offer.offerer_name : ''}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--on-surface-3)', marginTop: '2px', textTransform: offer.status === 'pending' ? 'none' : 'capitalize' }}>
                          {offer.status === 'pending' ? t('available_from') + ' ' + formatLocalTime(offer.offered_from, timezone, 'time') : offer.status}
                        </div>
                      </div>
                      {offer.status === 'pending' && (
                        <button onClick={() => onAcceptOffer(offer.id)} disabled={isLoading} style={{
                          padding: '10px 16px', borderRadius: '8px', border: 'none',
                          background: '#10b981', color: '#fff', fontSize: '13px', fontWeight: 700,
                          cursor: isLoading ? 'default' : 'pointer', opacity: isLoading ? 0.6 : 1, flexShrink: 0,
                        }}>
                          {isLoading ? t('accepting') : t('accept')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              {req.status === 'pending' && (
                <button onClick={onEdit} style={{
                  flex: 1, height: '44px', borderRadius: '10px',
                  border: '1.5px solid var(--primary)', background: '#fff',
                  color: 'var(--primary)', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}>
                  {t('edit_details')}
                </button>
              )}
              <button onClick={onCancel} disabled={isLoading} style={{
                flex: 1, height: '44px', borderRadius: '10px', border: 'none',
                background: '#dc2626', color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: isLoading ? 'default' : 'pointer', opacity: isLoading ? 0.7 : 1,
              }}>
                {isLoading ? t('cancelling') : t('cancel')}
              </button>
            </div>
          </>
        ) : (
          <>
            {mySpots.length > 1 && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--on-surface)' }}>
                  {t('which_spot_offer')}
                </label>
                <select value={offeringSpotId} onChange={(e) => setOfferingSpotId(e.target.value)} style={{
                  width: '100%', padding: '12px', borderRadius: '8px',
                  border: '1px solid #abb3b7', fontSize: '14px', boxSizing: 'border-box', cursor: 'pointer',
                }}>
                  <option value="">{t('select_spot')}</option>
                  {mySpots.map((spot) => (
                    <option key={spot.id} value={spot.id}>
                      {t(spot.level_code) || spot.level_label} · {spot.spot_number}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={onIgnore} style={{
                flex: 1, height: '44px', borderRadius: '10px',
                border: '1.5px solid #e3e9ec', background: '#fff',
                color: 'var(--on-surface-2)', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}>
                {t('ignore')}
              </button>
              <button
                onClick={() => {
                  const spotId = mySpots.length === 1 ? mySpots[0]?.id : offeringSpotId;
                  if (!spotId) return;
                  onOffer(spotId);
                }}
                disabled={createLoading || mySpots.length === 0 || (mySpots.length > 1 && !offeringSpotId)}
                style={{
                  flex: 2, height: '44px', borderRadius: '10px', border: 'none',
                  background: 'var(--primary)', color: '#fff',
                  fontSize: '14px', fontWeight: 700,
                  cursor: createLoading ? 'default' : 'pointer',
                  opacity: createLoading || mySpots.length === 0 || (mySpots.length > 1 && !offeringSpotId) ? 0.6 : 1,
                  boxShadow: '0 2px 8px -2px rgba(0,91,192,0.3)',
                }}
              >
                {createLoading ? t('offering') : t('offer_your_spot')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── CreateSheet ──────────────────────────────────────────────────────────────

function CreateSheet({
  from, setFrom, until, setUntil,
  isEditMode, createLoading,
  onSubmit, onQuickRequest, onClose, t,
}: {
  from: string; setFrom: (v: string) => void;
  until: string; setUntil: (v: string) => void;
  isEditMode: boolean;
  createLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onQuickRequest: (hours: number) => void;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [expandCustom, setExpandCustom] = useState(isEditMode);
  const minDateTime = new Date().toISOString().slice(0, 16);

  // Seed `until` to from+15min as a CONVENIENCE — but only when `until` is
  // still empty. Without the `!until` guard, every change to `from` wipes
  // whatever the user just typed into `until`. That's what caused the
  // "editing doesn't help" symptom: typing a custom end time was silently
  // overwritten on the next render cycle that touched `from`.
  useEffect(() => {
    if (from && !isEditMode && !until) {
      try {
        const [d, time] = from.split('T');
        const [y, m, day] = d.split('-');
        const [h, mm] = time.split(':');
        const dt = new Date();
        dt.setFullYear(parseInt(y), parseInt(m) - 1, parseInt(day));
        dt.setHours(parseInt(h), parseInt(mm) + 15, 0, 0);
        const pad = (n: number) => String(n).padStart(2, '0');
        setUntil(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
      } catch {}
    }
  }, [from, isEditMode, until, setUntil]);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        background: '#fff',
        borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
        padding: '20px 24px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 32px)',
        zIndex: 301,
        maxWidth: '430px', margin: '0 auto',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 -8px 32px -4px rgba(43,52,55,0.18)',
      }}>
        <div style={{ width: '40px', height: '4px', background: '#e3e9ec', borderRadius: '2px', margin: '0 auto 16px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'var(--manrope)', fontSize: '20px', fontWeight: 700, color: 'var(--on-surface)', margin: 0 }}>
            {isEditMode ? t('update_request_details') : t('create_new_request')}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--on-surface-3)' }} aria-label={t('close')}>
            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>close</span>
          </button>
        </div>

        {!isEditMode && (
          <>
            <h4 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--on-surface-2)', margin: '0 0 12px' }}>{t('quick_requests')}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              {[1, 2, 4].map((h) => (
                <button
                  key={h}
                  onClick={() => onQuickRequest(h)}
                  disabled={createLoading}
                  style={{
                    padding: '14px 8px', borderRadius: '12px', border: 'none',
                    background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                    color: '#fff', fontSize: '13px', fontWeight: 700,
                    cursor: createLoading ? 'default' : 'pointer',
                    opacity: createLoading ? 0.6 : 1,
                    boxShadow: '0 4px 12px rgba(0,91,192,0.2)',
                  }}
                >
                  {h === 1 ? t('up_to_1_hour') : h === 2 ? t('up_to_2_hours') : t('up_to_4_hours')}
                </button>
              ))}
            </div>
            <button
              onClick={() => setExpandCustom(!expandCustom)}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '12px',
                border: '1.5px solid #e3e9ec', background: '#fff',
                color: 'var(--on-surface)', fontSize: '14px', fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: expandCustom ? '12px' : '0',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--primary)' }}>edit</span>
                {t('custom_timeframe')}
              </span>
              <span className="material-symbols-outlined" style={{
                fontSize: '20px', color: 'var(--primary)',
                transition: 'transform 0.2s', transform: expandCustom ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>expand_more</span>
            </button>
          </>
        )}

        {(expandCustom || isEditMode) && (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--on-surface)' }}>{t('from')}</label>
              <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} min={minDateTime} required style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: '1px solid #abb3b7', fontSize: '14px', boxSizing: 'border-box',
              }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: 'var(--on-surface)' }}>{t('until')}</label>
              <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} min={from || minDateTime} required style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: '1px solid #abb3b7', fontSize: '14px', boxSizing: 'border-box',
              }} />
            </div>
            <button type="submit" disabled={createLoading} style={{
              padding: '14px', borderRadius: '10px', border: 'none',
              background: 'var(--primary)', color: '#fff',
              fontSize: '15px', fontWeight: 700,
              cursor: createLoading ? 'default' : 'pointer',
              opacity: createLoading ? 0.7 : 1,
              marginTop: '8px',
            }}>
              {createLoading ? (isEditMode ? t('updating') : t('creating')) : (isEditMode ? t('update_request') : t('create_request'))}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--on-surface-3)',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '20px',
  lineHeight: 1,
};
